import React, { useState, useRef, useImperativeHandle, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuthController, useStorageSource, useSnackbarController } from "@camberi/firecms";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Backdrop from "@mui/material/Backdrop";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import { TextField } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import LoadingButton from "@mui/lab/LoadingButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import FullscreenDragOverlay from "./FullscreenDragOverlay";

const Item = styled(Paper)(({ theme }) => ({
	backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
	...theme.typography.body2,
	padding: theme.spacing(1),
	textAlign: "left",
	paddingLeft: "15px",
	color: theme.palette.text.secondary,
}));

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

interface UploadFile {
	id: string;
	file: File;
	name: string;
	size: number;
	type: string;
	uploadProgress: number;
}

export const VersionUploader = React.forwardRef(function VersionUploader(
	{
		assetsFiltered,
		setForceRefresh,
	}: {
		assetsFiltered: any;
		setForceRefresh: any;
	},
	ref
) {
	const authController = useAuthController();
	const SnackbarController = useSnackbarController();
	const storage = useStorageSource();

	const enabled = assetsFiltered?.length === 1;

	const [newFiles, setNewFiles] = React.useState<UploadFile[]>([]);
	const [isWindowDragOver, setIsWindowDragOver] = useState(false);
	const [onUpload, setOnUpload] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const commentField = useRef<HTMLInputElement | null>(null);

	const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {};

	const handleFiles = useCallback(
		(fileList: FileList) => {
			const maxAllowedFileSize = 300; // MB
			const maxAllowedBytes = maxAllowedFileSize * 1024 * 1024;

			const filesArray = Array.from(fileList);

			const existingFileNames = newFiles.map((f) => f.name); // Assuming 'files' is state holding uploaded files

			const filesName: string[] = [];
			const invalidFileNames: string[] = [];
			let maxFileSize = 0;

			for (const file of filesArray) {
				filesName.push(file.name);
				if (file.size > maxFileSize) maxFileSize = file.size;

				if (!/^[a-zA-Z0-9_ ().\-\][]*$/.test(file.name)) {
					invalidFileNames.push(file.name);
				}
			}

			// Check max file size
			if (maxFileSize > maxAllowedBytes) {
				SnackbarController.open({
					type: "error",
					title: "Upload Failed",
					message: `Maximum file size exceeded: ${maxAllowedFileSize}MB`,
				});
				return;
			}

			// Check for invalid characters
			if (invalidFileNames.length > 0) {
				SnackbarController.open({
					type: "error",
					title: "Upload Failed",
					message: `Special characters are not allowed in filenames: ${invalidFileNames.join(", ")}`,
				});
				return;
			}

			// Check for duplicates within the current batch
			const findDuplicates = (arr: string[]) => arr.filter((item, index) => arr.indexOf(item) !== index && arr.indexOf(item) === index);
			const duplicatedFilenames = findDuplicates(filesName);

			if (duplicatedFilenames.length > 0) {
				SnackbarController.open({
					type: "error",
					title: "Upload Failed",
					message: `Each file needs a unique name. Duplicates found: ${duplicatedFilenames.join(", ")}`,
				});
				return;
			}

			// Filter out files already in the existing list
			const uniqueNewFiles = filesArray.filter((file) => !existingFileNames.includes(file.name));

			if (uniqueNewFiles.length === 0) {
				SnackbarController.open({
					type: "info",
					title: "No New Files",
					message: `All selected files are already added.`,
				});
				return;
			}

			const uploadFiles: UploadFile[] = uniqueNewFiles.map((file) => ({
				id: Math.random().toString(36).substring(2, 9),
				file,
				name: file.name,
				size: file.size,
				type: file.type,
				uploadProgress: 0,
			}));

			// setFiles((prev) => [...prev, ...uniqueNewFiles]);
			setNewFiles((prev) => [...prev, ...uploadFiles]);
			setForceRefresh(new Date());
		},
		[newFiles, setForceRefresh, SnackbarController]
	);

	const handleFileInput = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFiles = e.target.files;
			if (selectedFiles && selectedFiles.length > 0) {
				handleFiles(selectedFiles);
			}
		},
		[handleFiles]
	);

	const clearComment = () => {
		if (commentField.current) {
			commentField.current.value = "";
		}
	};

	const clearInput = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const removeFile = (fileId: string) => {
		setNewFiles((prev: UploadFile[]) => {
			const next = prev.filter((f) => f.id !== fileId);

			if (next.length === 0) {
				clearComment();
			}
			return next;
		});

		// Clear input value to allow re-uploading same files
		clearInput();
		setForceRefresh(new Date());
	};

	const clearAllFiles = () => {
		setNewFiles([]);
		clearComment();
		clearInput();
	};

	// Window-level drag and drop handlers
	const handleWindowDragOver = useCallback((e: DragEvent) => {
		e.preventDefault();
		setIsWindowDragOver(true);
	}, []);

	const handleWindowDragLeave = useCallback((e: DragEvent) => {
		e.preventDefault();
		// Only hide overlay if leaving the window entirely
		if (e.clientX <= 0 || e.clientY <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
			setIsWindowDragOver(false);
		}
	}, []);

	const handleWindowDrop = useCallback(
		(e: DragEvent) => {
			e.preventDefault();
			setIsWindowDragOver(false);

			if (enabled && e.dataTransfer && e.dataTransfer.files.length > 0) {
				handleFiles(e.dataTransfer.files);
			}
		},
		[handleFiles, enabled]
	);

	// Add window event listeners
	useEffect(() => {
		const handleDragEnter = (e: DragEvent) => {
			e.preventDefault();
			setIsWindowDragOver(true);
		};

		window.addEventListener("dragover", handleWindowDragOver);
		window.addEventListener("dragenter", handleDragEnter);
		window.addEventListener("dragleave", handleWindowDragLeave);
		window.addEventListener("drop", handleWindowDrop);

		return () => {
			window.removeEventListener("dragover", handleWindowDragOver);
			window.removeEventListener("dragenter", handleDragEnter);
			window.removeEventListener("dragleave", handleWindowDragLeave);
			window.removeEventListener("drop", handleWindowDrop);
		};
	}, [handleWindowDragOver, handleWindowDragLeave, handleWindowDrop]);

	const handleClose = () => {};

	const putStorageItem = (file: UploadFile, storagePath: string = "") => {
		return new Promise(function (resolve, reject) {
			const metadata = "";
			const fileName = file.name;
			// the return value will be a Promise

			return storage
				.uploadFile({
					file: file.file,
					fileName,
					path: storagePath,
					metadata,
				})
				.then(async (data) => {
					resolve({ file: file, data: data });
				})
				.catch((err) => {
					reject({ file: file, data: err });
				});
		});
	};

	// const fileInputRef = useRef<HTMLInputElement | null>(null);

	const onButtonClick = () => {
		// `current` points to the mounted file input element
		//fileInputRef.click();
		if (fileInputRef.current != null) {
			fileInputRef.current.click();
		}
	};

	useImperativeHandle(ref, () => ({
		openDialog: () => {
			onButtonClick();
		},
	}));

	const requestNewVersion = async () => {
		const asset_id: string = assetsFiltered[0].id,
			announced_filecount: number = newFiles.length,
			announced_files: any = newFiles.map((file) => [file.name, file.type]),
			commit_message: string = typeof commentField.current?.value === "undefined" ? "" : commentField.current?.value === null ? "" : commentField.current?.value,
			origin: string = "webapp";

		setOnUpload(true);
		let respData;
		try {
			const response = await axios({
				url: `${authController?.extra?.apiUrl}/version/create`,
				method: "POST",
				headers: {
					Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}`,
				},
				data: { asset_id, commit_message, announced_filecount, announced_files, origin },
			});

			respData = await response.data;

			if (respData.success) {
				if (typeof respData.bucket_id !== "undefined" && respData.bucket_id !== "") {
					const uploadPath = "versions/" + respData.bucket_id + "/source";

					//starting upload
					const allFileUpload = await uploadFiles(newFiles, uploadPath);

					if (allFileUpload.success) {
						SnackbarController.open({
							type: "success",
							title: "Done",
							message: `${allFileUpload.message}`,
						});
						clearAllFiles();
						// setNewFiles([]);
						setOnUpload(false);
						setForceRefresh(new Date());
					} else {
						SnackbarController.open({
							type: "error",
							title: "Upload failed",
							message: `${allFileUpload.message}`,
						});
						setOnUpload(false);
						setForceRefresh(new Date());
					}
				} else {
					SnackbarController.open({
						type: "error",
						title: "Upload failed",
						message: `Bucketpath invalid`,
					});
					setOnUpload(false);
					setForceRefresh(new Date());
				}
			} else {
				SnackbarController.open({
					type: "error",
					title: "Upload failed",
					message: `Error: ${respData.message}`,
				});
				setOnUpload(false);
				setForceRefresh(new Date());
			}
		} catch (err: any) {
			setOnUpload(false);
			setForceRefresh(new Date());

			const message = typeof err.response?.data?.message !== "undefined" ? err.response.data.message : "Failed to create version";

			SnackbarController.open({
				type: "error",
				title: "Upload failed",
				message: message,
			});
			console.log(err);
		}
	};

	const uploadFiles = async (files: UploadFile[], storagePath: string) => {
		for (const file of files) {
			const newFile = await putStorageItem(file, storagePath)
				.then((path) => {
					const resp = { success: true, message: path };
					console.log(resp);

					return resp;
				})
				.catch((err) => {
					return { success: false, message: err.data.message };
				});

			if (!newFile.success) {
				return newFile;
			}
		}

		return { success: true, message: "All files uploaded" };
	};

	const emptyVersion = enabled ? (typeof assetsFiltered[0].values.latest_version !== "undefined" ? Object.keys(assetsFiltered[0].values.latest_version).length === 0 : true) : true;

	const nextVersion = emptyVersion ? 1 : assetsFiltered[0]?.values.latest_version ? assetsFiltered[0]?.values.latest_version?.number + 1 : 1;
	// const nextVersionString = nextVersion < 10 ? "00" + nextVersion.toString() : nextVersion < 100 ? "0" + nextVersion.toString() : "" + nextVersion.toString();

	const author_name_arr = authController?.extra?.user_data?.values?.email?.split("@")[0]?.split(".");
	// const author_caps = (author_name_arr?.length > 1 ? author_name_arr[0][0] + "" + author_name_arr[author_name_arr.length - 1][0] : typeof author_name_arr !== "undefined" ? author_name_arr[0] : "").toUpperCase();
	// const author_name = typeof author_name_arr === "object" ? capitalizeWords(author_name_arr).join(" ") : "";

	return (
		<>
			{isWindowDragOver &&
				(enabled ? (
					<FullscreenDragOverlay header={"Add files"} message={"Drop any media file here to it to the version"} icon={<AddCircleIcon color={"secondary"} sx={{ fontSize: "5em" }} />} />
				) : (
					<FullscreenDragOverlay header={"No asset selected"} message={"Select an asset to upload version"} icon={<HighlightOffIcon color={"error"} sx={{ fontSize: "5em" }} />} />
				))}

			<Box sx={{ maxWidth: "100%", position: "absolute", overflow: "hidden", bottom: "0px", mt: "auto", zIndex: 3 }}>
				<input type="file" accept="image/png, image/jpeg, image/gif, video/*" id="file" onChange={handleFileInput} ref={fileInputRef} multiple style={{ display: "none" }} />

				<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={newFiles.length > 0} onClick={handleClose} />
				<Accordion sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, borderRadius: "15px" }} disableGutters expanded={newFiles.length > 0} onChange={handleChange("panel1")}>
					<AccordionSummary disabled={!enabled} color={"primary"} aria-controls="panel1bh-content" id="panel1bh-header" onClick={onButtonClick}>
						<Box sx={{ display: "flex", height: "50px", alignItems: "center" }} id="someRandomID">
							{!enabled ? null : (
								<Tooltip title="Add files">
									<IconButton color={"secondary"}>
										<AddCircleIcon />
									</IconButton>
								</Tooltip>
							)}
							<Box>
								{!enabled ? (
									<Typography align="center">Select single asset to upload version</Typography>
								) : (
									<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pr: 1 }}>
										<Typography variant="body2">Upload</Typography>

										<Typography variant="body2" color="text.primary" align={"left"} sx={{ bgcolor: "rgba(0,0,0,0.5)", ml: 1, pl: 1, pr: 1, pt: 0.5, pb: 0.5, borderRadius: "4px" }}>
											<b>{assetsFiltered[0]?.values.name}</b>
										</Typography>
										<Typography variant="body2" color="text.primary" align={"left"} sx={{ bgcolor: "rgba(255,255,255,0.08)", ml: 0.5, pl: 1, pr: 1, pt: 0.5, pb: 0.5, borderRadius: "4px" }}>
											<b>v{nextVersion}</b>
										</Typography>
									</Box>
								)}
							</Box>
						</Box>
					</AccordionSummary>
					<Divider />
					<AccordionDetails
						sx={{
							maxHeight: "350px",
							ml: "5px",
							pl: 2,
							pr: 2,
							alignItems: "center",
							overflowX: "hidden",
							overflowY: "scroll",
							"&::-webkit-scrollbar": { background: "rgba(0,0,0,0)", width: "5px" },
							"&::-webkit-scrollbar-thumb": { background: "rgba(125,125,125,1)", borderRadius: "5px" },
						}}
					>
						<Box component="span">
							{newFiles?.map((file: UploadFile, Index) => {
								const fileType = file.type.split("/")[1];
								const fileSize = formatFileSize(file.size);
								return (
									<Item key={file.name + "_" + Index}>
										<b>{file.name}</b>
										<br />
										<Typography variant="caption">
											{fileSize} &#183; {fileType}
										</Typography>
										<IconButton
											sx={{ height: "30px", width: "30px" }}
											onClick={() => {
												removeFile(file.id);
											}}
										>
											{onUpload ? null : <HighlightOffIcon sx={{ height: "20px", width: "20px" }} />}
										</IconButton>
									</Item>
								);
							})}
						</Box>
					</AccordionDetails>
					<Divider sx={{ mb: 2 }} />
					<Box sx={{ pl: 3, pr: 3 }}>
						<TextField label="Optional version comment" color="primary" focused key="password" style={{ width: "100%", textAlign: "left" }} placeholder="(Optional) Description e.g. - changed lighting" multiline rows={5} inputRef={commentField} />
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3, mt: 2 }}>
						<LoadingButton
							sx={{ whiteSpace: "nowrap", color: "text.primary", textTransform: "none", pl: 10, pr: 10 }}
							loading={onUpload}
							color="secondary"
							onClick={requestNewVersion}
							size={"medium"}
							loadingPosition="end"
							variant="contained"
							endIcon={<CloudUploadIcon />}
						>
							{!onUpload ? "Create Version" : "Uploading..."}
						</LoadingButton>
					</Box>
				</Accordion>
			</Box>
		</>
	);
});
